/* FILE: ./frontend/src/hooks/useCompliance.js */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useCompliance() {
  // ⚡ FIX: Initialize with SAFE empty arrays so it never crashes
  const [rules, setRules] = useState({ 
      hsPrefixes: [], 
      keywords: [], 
      categories: [] // <--- Critical: Must exist initially
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadRules = async () => {
      try {
        // 1. Check Local Storage Cache (Optional Optimization)
        const cached = localStorage.getItem('compliance_rules');
        const timestamp = localStorage.getItem('compliance_rules_time');
        const ONE_DAY = 24 * 60 * 60 * 1000;

        if (cached && timestamp && (Date.now() - Number(timestamp) < ONE_DAY)) {
             setRules(JSON.parse(cached));
             setLoading(false);
             return;
        }

        // 2. Fetch from DB
        console.log("🔒 Security: Fetching Compliance Rules...");
        const { data, error } = await supabase.from('compliance_rules').select('*');

        if (error) throw error;

        if (data) {
          const processed = {
            hsPrefixes: data.filter(r => r.rule_type === 'hs_prefix'),
            keywords: data.filter(r => r.rule_type === 'keyword'),
            // ⚡ FILTER CATEGORIES
            categories: data.filter(r => r.rule_type === 'category')
          };
          
          setRules(processed);
          
          // Update Cache
          localStorage.setItem('compliance_rules', JSON.stringify(processed));
          localStorage.setItem('compliance_rules_time', Date.now().toString());
        }
      } catch (err) {
        console.error("❌ Security Logic Failed to Load:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadRules();
  }, []);

  // ⚡ Helper to get rule for a specific category selection
  const getCategoryRule = useCallback((categoryName) => {
      // Safety check using optional chaining
      return rules.categories?.find(c => c.value === categoryName);
  }, [rules.categories]);

  /**
   * The Watchdog Function
   */
  const checkCompliance = useCallback((textToCheck = '', hsCodeToCheck = '') => {
    // FAIL-CLOSED: If logic is still loading or broken, generally safe to return false here 
    // unless you want strict blocking during load.
    if (loading || error) return { restricted: false, reason: '' };

    // A. Iron Gate Check (HS Codes)
    if (hsCodeToCheck) {
        const cleanHs = hsCodeToCheck.replace(/\D/g, '');
        if (cleanHs.length >= 2) {
          const prefix = cleanHs.substring(0, 2);
          const match = rules.hsPrefixes.find(r => r.value === prefix);
          if (match) {
            return { restricted: true, reason: `HS Chapter ${prefix}: ${match.reason_message}` };
          }
        }
    }

    // B. Trojan Horse Check (Keywords)
    if (textToCheck) {
      const lowerText = textToCheck.toLowerCase();
      // Scan for every keyword in our database
      const match = rules.keywords.find(r => lowerText.includes(r.value.toLowerCase()));
      if (match) {
        return { restricted: true, reason: `Prohibited Term: "${match.value}"` };
      }
    }

    return { restricted: false, reason: '' };
  }, [loading, error, rules]);

  // ⚡ EXPORT 'rules' SO THE FRONTEND CAN BUILD DROPDOWNS
  return { checkCompliance, getCategoryRule, rules, loading };
}